import _ from 'lodash';
import * as SmartySDK from 'smartystreets-javascript-sdk';
import { SMARTY_API_KEY, ENV } from '../../constants.js';
import { addressToString } from '../../utils/address_utils.js';

class AddressHandler {
    constructor(isMockMode) {
        this.isMockMode = false;
        this.SmartyCore = SmartySDK.core;
        this.smartySharedCredentials = new this.SmartyCore.SharedCredentials(SMARTY_API_KEY);
        this.autoCompleteClient = new this.SmartyCore.ClientBuilder(this.smartySharedCredentials)
            .withLicenses(['us-autocomplete-pro-cloud'])
            .buildUsAutocompleteProClient();
        this.usStreetClient = new this.SmartyCore.ClientBuilder(this.smartySharedCredentials).buildUsStreetApiClient();
        if (isMockMode) {
            this.isMockMode = isMockMode;
        }
        else {
            const curEnv = import.meta.env.VITE_CURR_ENV;
            this.isMockMode = curEnv === ENV.DEVELOPMENT || curEnv === ENV.STAGING;
        }
    }
    /**
     * Finds address suggestions using SmartyStreets US Autocomplete Pro API.
     * @param query - The address query string.
     * @param maxSearchResults - Maximum number of search results to return.
     * @returns - A promise that resolves to an array of address suggestions.
     */
    async findSuggestions(query, maxSearchResults = 5) {
        if (this.isMockMode) {
            return this.mock_findSuggestions(query, maxSearchResults);
        }
        else {
            return this.smarty_findSuggestions(query, maxSearchResults);
        }
    }
    async smarty_findSuggestions(query, maxSearchResults) {
        const lookup = new SmartySDK.usAutocompletePro.Lookup(query);
        lookup.maxResults = maxSearchResults;
        lookup.preferGeolocation = 'none';
        try {
            await this.autoCompleteClient.send(lookup);
        }
        catch (error) {
            console.error('Error fetching autocomplete suggestions:', error);
            lookup.result = [];
        }
        return _.uniqBy(lookup.result, (item) => `${item.streetLine}|${item.city}|${item.state}|${item.zipcode}`);
    }
    async mock_findSuggestions(query, maxSearchResults) {
        console.log('Mock findSuggestions called with query:', query);
        // Delay to simulate network latency
        await new Promise((resolve) => setTimeout(resolve, 200));
        // In mock mode, return dummy suggestions
        const suggestions = [];
        for (let i = 1; i <= maxSearchResults; i++) {
            // Attempt to extract a state code from the query
            const state = query.split(' ').find((w) => w.trim().length === 2);
            suggestions.push({
                streetLine: '12345 Mock St',
                secondary: '# ' + i,
                city: 'Mock City',
                state: state ?? 'NY',
                zipcode: '12345-6789',
                entries: 1,
            });
        }
        return suggestions;
    }
    /**
     * Finds detailed address information using Smarty US Street API.
     * @param client - The Smarty US Street API client.
     * @param address - The address to look up.
     * @returns - The detailed Address object.
     */
    async findDetailedAddress(input) {
        if (this.isMockMode) {
            return this.mock_findDetailedAddress(input);
        }
        else {
            return this.smarty_findDetailedAddress(input);
        }
    }
    async smarty_findDetailedAddress(input) {
        const streetLookup = new SmartySDK.usStreet.Lookup();
        streetLookup.street = input.addressLineOne;
        streetLookup.secondary = input.addressLineTwo;
        streetLookup.city = input.city;
        streetLookup.state = input.state;
        streetLookup.zipCode = input.zip;
        const results = await this.usStreetClient.send(streetLookup);
        if (results.lookups.length > 0 && results.lookups[0].result.length > 0) {
            const details = results.lookups[0].result[0];
            const zipBase = details.components.zipCode;
            const zipExtension = details.components.plus4Code;
            const addressLineOne = [
                details.components.primaryNumber,
                details.components.streetPredirection,
                details.components.streetName,
                details.components.streetSuffix,
                details.components.streetPostdirection,
            ]
                .filter(Boolean)
                .join(' ')
                .trim();
            const addressLineTwo = [
                details.components.secondaryDesignator,
                details.components.secondaryNumber,
            ]
                .filter(Boolean)
                .join(' ')
                .trim();
            const address = {
                addressLineOne,
                addressLineTwo,
                city: details.components.cityName,
                state: details.components.state,
                zip: zipExtension ? `${zipBase}-${zipExtension}` : zipBase,
                zipBase: zipBase,
                zipExtension: zipExtension,
                county: details.metadata?.countyName,
                lat: details.metadata?.latitude,
                lng: details.metadata?.longitude,
                country: input.country ?? 'US', // Implied by using the US Street API
                addressString: undefined,
            };
            address.addressString = addressToString(address);
            return address;
        }
        // If no results, return the input address as-is
        return input;
    }
    async mock_findDetailedAddress(input) {
        // Delay to simulate network latency
        await new Promise((resolve) => setTimeout(resolve, 200));
        // In mock mode, simply return the input address with some dummy data
        return {
            ...input,
            zipBase: input.zip ? input.zip.split('-')[0] : undefined,
            zipExtension: input.zip && input.zip.includes('-') ? input.zip.split('-')[1] : undefined,
            county: 'Mock County',
            lat: 40.7128,
            lng: -74.006,
            country: input.country ?? 'US',
            addressString: addressToString(input),
        };
    }
}

export { AddressHandler };
//# sourceMappingURL=AddressHandler.js.map
